import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { AppContext } from '../../context/AppContext';
import { addWarmupArticle } from '../../services/WarmupArticleServiceFirestore';
import { getExportedImages, ExportedImage } from '../../services/thumbnailService';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';

const PageContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 32px 16px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(16,19,37,0.08);
  padding: 24px;
`;

const Button = styled.button`
  background: #e4312e;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #c3251a; }
`;

const OutputBox = styled.div`
  background: #181c2f;
  color: #fff;
  border-radius: 10px;
  margin-top: 24px;
  padding: 20px;
  font-size: 1.15rem;
  min-height: 80px;
  white-space: pre-wrap;
`;

const ErrorMsg = styled.div`
  color: #e4312e;
  margin-top: 10px;
  font-weight: bold;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

const ImageGrid = styled.div`
  margin-top: 15px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 15px;
`;

const ImageCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;
  &:hover {
    transform: translateY(-5px);
  }
`;

const ImagePreview = styled.div`
  height: 150px;
  overflow: hidden;
  position: relative;
`;

const ImageInfo = styled.div`
  padding: 10px;
`;

const ImageTitle = styled.div`
  margin-bottom: 5px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ImageDate = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 10px;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 8px 0;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    background-color: #2980b9;
  }
`;

const CoverImageContainer = styled.div`
  margin-bottom: 16px;
  border: 1px dashed #ccc;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CoverImagePreview = styled.div`
  width: 100%;
  max-height: 200px;
  overflow: hidden;
  margin-bottom: 10px;
  border-radius: 4px;
  img {
    width: 100%;
    height: auto;
    object-fit: cover;
  }
`;

const EditorContainer = styled.div`
  margin: 16px 0;
  
  .w-md-editor {
    background-color: #ffffff;
  }
  
  .w-md-editor-text-input,
  .w-md-editor-text {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
  }
  
  .w-md-editor-preview {
    padding: 16px;
  }

  @media (max-width: 768px) {
    .w-md-editor {
      .w-md-editor-text-input,
      .w-md-editor-text {
        font-size: 16px !important; /* Evita zoom en móviles */
      }
    }
  }
`;

const Blog: React.FC = () => {
  const { data } = useContext(AppContext);
  const { currentUser } = useAuth();
  const [content, setContent] = useState(`# Mi Primer Artículo

Escribe aquí tu artículo usando **Markdown**. Puedes usar diferentes formatos:

## Formatos de texto
- **Texto en negrita**
- *Texto en cursiva*
- ~~Texto tachado~~

## Listas
1. Primer elemento
2. Segundo elemento
3. Tercer elemento

### Lista con viñetas
- Elemento uno
- Elemento dos
- Elemento tres

## Enlaces y citas
Puedes agregar [enlaces a sitios web](https://example.com).

> Esta es una cita destacada que resalta información importante.

---

¡Borra este contenido de ejemplo y escribe tu artículo!`);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [exportedImages, setExportedImages] = useState<ExportedImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedCoverImage, setSelectedCoverImage] = useState<ExportedImage | null>(null);

  // Cargar imágenes exportadas del usuario actual
  const loadExportedImages = async () => {
    if (!currentUser?.uid) return;
    
    setLoadingImages(true);
    try {
      const images = await getExportedImages(currentUser.uid);
      setExportedImages(images);
    } catch (err) {
      console.error('Error al cargar imágenes:', err);
      setError('No se pudieron cargar las imágenes guardadas');
    } finally {
      setLoadingImages(false);
    }
  };

  // Abrir modal de selección de imagen
  const handleOpenImageModal = () => {
    setShowImageModal(true);
    loadExportedImages();
  };

  // Seleccionar imagen como portada
  const handleSelectCoverImage = (image: ExportedImage) => {
    setSelectedCoverImage(image);
    setShowImageModal(false);
  };

  // Eliminar imagen de portada seleccionada
  const handleRemoveCoverImage = () => {
    setSelectedCoverImage(null);
  };

  const handlePublish = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    
    if (!title.trim()) {
      setError('El título es obligatorio');
      setLoading(false);
      return;
    }
    
    if (!content.trim()) {
      setError('El contenido es obligatorio');
      setLoading(false);
      return;
    }
    
    try {
      // Guardar en Firebase y publicar en WarmUp
      await addWarmupArticle({
        ligaId: '',  // No está asociado a una liga específica
        ligaNombre: 'Blog',
        circuito: '',  // No está asociado a un circuito específico
        fecha: new Date().toISOString(),
        autor: currentUser?.displayName || 'Redacción',
        contenidoMarkdown: content,
        createdAt: Date.now(),
        titulo: title,
        coverImageUrl: selectedCoverImage?.imageUrl, // Añadir URL de la imagen de portada si está seleccionada
      });
      
      setSuccess(true);
      // Limpiar el formulario después de publicar exitosamente
      setTitle('');
      setContent('');
      setSelectedCoverImage(null);
    } catch (err: any) {
      console.error('Error al publicar:', err);
      setError(err.message || 'Error al publicar el blog');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <h2>📝 Editor de Blog</h2>
      <p style={{ color: '#666', marginBottom: '24px' }}>
        Escribe tus noticias usando Markdown. Puedes usar <strong>**negritas**</strong>, <em>*cursivas*</em>, 
        listas, enlaces y más. La vista previa te muestra cómo se verá el resultado final.
      </p>
      
      <Form onSubmit={handlePublish}>
        {/* Sección de imagen de portada */}
        <CoverImageContainer>
          <h3>📸 Imagen de Portada</h3>
          {selectedCoverImage ? (
            <>
              <CoverImagePreview>
                <img src={selectedCoverImage.imageUrl} alt="Imagen de portada" />
              </CoverImagePreview>
              <div style={{ display: 'flex', gap: '10px' }}>
                <Button type="button" onClick={handleRemoveCoverImage} style={{ backgroundColor: '#e74c3c' }}>
                  Eliminar Imagen
                </Button>
                <Button type="button" onClick={handleOpenImageModal}>
                  Cambiar Imagen
                </Button>
              </div>
            </>
          ) : (
            <Button type="button" onClick={handleOpenImageModal}>
              Seleccionar Imagen de Portada
            </Button>
          )}
        </CoverImageContainer>

        <input
          type="text"
          placeholder="Título del blog"
          value={title}
          onChange={e => setTitle(e.target.value)}
          required
          style={{ 
            fontSize: '1.1rem', 
            padding: '12px', 
            borderRadius: '8px', 
            border: '1px solid #ccc',
            marginBottom: '16px',
            width: '100%'
          }}
        />

        <EditorContainer>
          <MDEditor
            value={content}
            onChange={(val) => setContent(val || '')}
            preview="edit"
            hideToolbar={false}
            height={400}
            data-color-mode="light"
          />
          <div style={{ 
            padding: '8px 12px', 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            fontSize: '12px',
            color: '#6c757d'
          }}>
            💡 <strong>Ayuda de formato:</strong> **negrita**, *cursiva*, # Título, - Lista, [enlace](url), &gt; Cita
          </div>
        </EditorContainer>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '16px' }}>
          <Button type="submit" disabled={loading}>
            {loading ? '📤 Publicando...' : '🚀 Publicar Blog'}
          </Button>
          
          <Button 
            type="button" 
            onClick={() => {
              // Cambiar a modo vista previa completa
              const editor = document.querySelector('.w-md-editor');
              if (editor) {
                const previewButton = editor.querySelector('[data-name="preview"]') as HTMLElement;
                if (previewButton) previewButton.click();
              }
            }}
            style={{ backgroundColor: '#3498db' }}
          >
            👁️ Vista Previa
          </Button>
        </div>

        {error && <ErrorMsg>{error}</ErrorMsg>}
        {success && <OutputBox>✅ ¡Blog publicado exitosamente! Tu entrada aparecerá en la página de WarmUp.</OutputBox>}
      </Form>

      {/* Modal de selección de imagen */}
      {showImageModal && (
        <Modal>
          <ModalContent>
            <h3>Mis Imágenes Guardadas</h3>
            <p>Selecciona una imagen para usar como portada de tu blog.</p>
            
            <Button type="button" onClick={() => setShowImageModal(false)} style={{ marginBottom: '15px', backgroundColor: '#7f8c8d' }}>
              Cancelar
            </Button>
            
            {loadingImages ? (
              <p>Cargando imágenes...</p>
            ) : exportedImages.length === 0 ? (
              <p>No tienes imágenes guardadas. Puedes crear imágenes en el Generador de Miniaturas.</p>
            ) : (
              <ImageGrid>
                {exportedImages.map((image) => (
                  <ImageCard key={image.id} onClick={() => handleSelectCoverImage(image)}>
                    <ImagePreview>
                      <img 
                        src={image.imageUrl} 
                        alt={image.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      />
                    </ImagePreview>
                    <ImageInfo>
                      <ImageTitle>{image.name}</ImageTitle>
                      <ImageDate>
                        {image.createdAt ? new Date((image.createdAt as any).seconds * 1000).toLocaleDateString() : 'Fecha desconocida'}
                      </ImageDate>
                      <SelectButton>Seleccionar</SelectButton>
                    </ImageInfo>
                  </ImageCard>
                ))}
              </ImageGrid>
            )}
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

export default Blog;
